/**
 * Shared dismiss behavior for floating toolbar panels (Visibilidad, Columnas).
 *
 * Contract (see openspec `stock-toolbar-panel-dismiss`):
 * - `click`       → close unless the target is inside the host (trigger included).
 * - `scroll`      → close unless the target is inside the panel. Registered on
 *                   `document` in the capture phase: `scroll` does not bubble
 *                   and the app shell window never scrolls, so only a capture
 *                   listener sees every scroller.
 * - `Escape`      → always close, even when the event originates inside the panel.
 *
 * Consumers attach while the panel is open and detach on close/destroy; this
 * helper owns no lifecycle of its own.
 */

/**
 * Minimal containment shape the helper needs. `Element` satisfies it, and so do
 * the plain DOM shims the unit specs install under bun:test.
 */
export interface FloatingPanelDismissContainer {
  contains(other: unknown): boolean;
}

export interface FloatingPanelDismissOptions {
  /** Element that counts as "inside" for click-outside (host incl. trigger and panel). */
  getHost: () => FloatingPanelDismissContainer | null;
  /** Element that counts as "inside" for scroll-inside (the scrollable panel itself). */
  getPanel: () => FloatingPanelDismissContainer | null;
  close: () => void;
}

export interface FloatingPanelDismiss {
  attach: () => void;
  detach: () => void;
}

export function createFloatingPanelDismiss(options: FloatingPanelDismissOptions): FloatingPanelDismiss {
  const isInside = (container: FloatingPanelDismissContainer | null, event: Event): boolean =>
    container !== null && container.contains(event.target);

  const handleClick = (event: Event): void => {
    if (!isInside(options.getHost(), event)) {
      options.close();
    }
  };

  const handleScroll = (event: Event): void => {
    if (!isInside(options.getPanel(), event)) {
      options.close();
    }
  };

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      options.close();
    }
  };

  return {
    attach(): void {
      document.addEventListener('scroll', handleScroll, true);
      document.addEventListener('click', handleClick);
      document.addEventListener('keydown', handleKeydown);
    },
    detach(): void {
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeydown);
    },
  };
}
